import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Edit3, Eye, Trash2, LayoutGrid } from 'lucide-react';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { TablePagination } from '@/components/ui/TablePagination';
import { cn } from '@/utils/cn';
import type { SettingsCategory } from '../types/categoriesList.types';

interface CategoryTableBlockProps {
  categories: SettingsCategory[];
  selectedCategoryId: string | null;
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onSelect: (category: SettingsCategory) => void;
  onEdit: (category: SettingsCategory) => void;
  onDelete: (category: SettingsCategory) => void;
  onDuplicate: (category: SettingsCategory) => void;
  onViewDetail: (category: SettingsCategory) => void;
}

const categoryColorMap: Record<string, string> = {
  work: 'bg-blue-50 text-blue-700 border-blue-100',
  material: 'bg-teal-50 text-teal-700 border-teal-100',
  equipment: 'bg-amber-50 text-amber-700 border-amber-100',
  personnel: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  service: 'bg-rose-50 text-rose-700 border-rose-100',
  department: 'bg-violet-50 text-violet-700 border-violet-100',
  other: 'bg-slate-100 text-slate-700 border-slate-200',
};

export function CategoryTableBlock({
  categories,
  selectedCategoryId,
  isLoading,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
  onViewDetail,
}: CategoryTableBlockProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const childrenByParent = useMemo(() => {
    const map = new Map<string, SettingsCategory[]>();
    for (const item of categories) {
      if (!item.parentId) continue;
      const list = map.get(item.parentId) || [];
      list.push(item);
      map.set(item.parentId, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'vi'));
    }
    return map;
  }, [categories]);

  const parentCategories = useMemo(
    () => categories
      .filter((item) => !item.parentId)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'vi')),
    [categories],
  );

  const pagedParents = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return parentCategories.slice(start, start + pageSize);
  }, [parentCategories, currentPage, pageSize]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderMobileChildren = (parentId: string, level: number): React.ReactNode => {
    const children = childrenByParent.get(parentId) || [];
    if (children.length === 0) return null;
    return (
      <div className="space-y-2">
        {children.map((item) => {
          const hasChildren = (childrenByParent.get(item.id) || []).length > 0;
          const isExpanded = expandedIds.has(item.id);
          return (
            <div key={item.id} className="space-y-2 rounded-md border border-slate-100 p-3">
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onSelect(item)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-xs md:text-sm text-slate-900">{item.name}</p>
                  <p className="text-xs text-slate-500">{item.code}</p>
                </button>
                <div className="shrink-0">
                  <StatusBadge status={item.status} />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span className={cn(
                  'inline-flex w-20 items-center justify-center rounded-md border py-0.5 text-xs shadow-sm',
                  categoryColorMap[item.type] || categoryColorMap.other
                )}>
                  {item.typeName}
                </span>
                <span>{item.itemCount}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-50" onClick={(event) => event.stopPropagation()}>
                <button
                  onClick={() => onViewDetail(item)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white py-2 text-xs font-medium text-slate-700 active:bg-slate-50"
                >
                  <Eye className="h-4 w-4" />
                  <span>Xem</span>
                </button>
                <button
                  onClick={() => onEdit(item)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white py-2 text-xs font-medium text-slate-700 active:bg-slate-50"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Sửa</span>
                </button>
                <button
                  onClick={() => onDuplicate(item)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white py-2 text-xs font-medium text-slate-700 active:bg-slate-50"
                >
                  <Copy className="h-4 w-4" />
                  <span>Chép</span>
                </button>
                <button
                  onClick={() => onDelete(item)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-md border border-red-100 bg-red-50 py-2 text-xs font-medium text-red-600 active:bg-red-100"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Xóa</span>
                </button>
              </div>
              {hasChildren ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(item.id)}
                    className="flex items-center gap-2 rounded-md border border-slate-100 p-3 text-sm text-slate-700"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span>Danh mục con</span>
                  </button>
                  {isExpanded ? <div className={cn('space-y-2', level > 1 && 'pl-3')}>{renderMobileChildren(item.id, level + 1)}</div> : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDesktopRows = (parentId: string, level: number): React.ReactNode => {
    const children = childrenByParent.get(parentId) || [];
    if (children.length === 0) return null;
    return children.map((item) => {
      const hasChildren = (childrenByParent.get(item.id) || []).length > 0;
      const isExpanded = expandedIds.has(item.id);
      return (
        <React.Fragment key={item.id}>
          <tr
            className={cn('cursor-pointer hover:bg-slate-50', selectedCategoryId === item.id && 'bg-emerald-50/50')}
            onClick={() => onSelect(item)}
          >
            <td className="p-3">
              <div className="flex items-center gap-2" style={{ paddingLeft: `${level * 16}px` }}>
                {hasChildren ? (
                  <button
                    type="button"
                    className="rounded-md border border-slate-100 p-1"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExpanded(item.id);
                    }}
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                ) : <span className="inline-block h-6 w-6" />}
                <div className="min-w-0">
                  <p className="truncate text-xs md:text-sm text-slate-900">{item.name}</p>
                  <p className="text-xs text-slate-500">{item.code}</p>
                </div>
              </div>
            </td>
            <td className="p-3">
              <span className={cn(
                'inline-flex w-20 items-center justify-center rounded-md border py-0.5 text-xs shadow-sm',
                categoryColorMap[item.type] || categoryColorMap.other
              )}>
                {item.typeName}
              </span>
            </td>
            <td className="p-3 text-center text-slate-700">{item.itemCount}</td>
            <td className="p-3"><StatusBadge status={item.status} /></td>
            <td className="p-3 text-right" onClick={(event) => event.stopPropagation()}>
              <ActionMenu
                items={[
                  { label: 'Xem chi tiết', icon: Eye, onClick: () => onViewDetail(item) },
                  { label: 'Chỉnh sửa', icon: Edit3, onClick: () => onEdit(item) },
                  { label: 'Nhân bản', icon: Copy, onClick: () => onDuplicate(item) },
                  { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => onDelete(item) },
                ]}
              />
            </td>
          </tr>
          {hasChildren && isExpanded ? renderDesktopRows(item.id, level + 1) : null}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="space-y-2">
      <div className="lg:hidden">
        <CardList
          items={pagedParents}
          isLoading={isLoading}
          gridClassName="grid-cols-1 md:grid-cols-2"
          renderCard={(item) => {
            const hasChildren = (childrenByParent.get(item.id) || []).length > 0;
            const isExpanded = expandedIds.has(item.id);
            return (
              <article key={item.id} className={cn('rounded-md border border-slate-100 bg-white p-3 shadow-sm', selectedCategoryId === item.id && 'ring-1 ring-emerald-300')}>
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onSelect(item)}>
                      <p className="truncate text-xs md:text-sm text-slate-900">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.code}</p>
                    </button>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span className={cn(
                      'inline-flex w-20 items-center justify-center rounded-md border py-0.5 text-xs shadow-sm',
                      categoryColorMap[item.type] || categoryColorMap.other
                    )}>
                      {item.typeName}
                    </span>
                    <span>{item.itemCount}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-50" onClick={(event) => event.stopPropagation()}>
                    <button
                      onClick={() => onViewDetail(item)}
                      className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white py-2 text-xs text-slate-700 active:bg-slate-50"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Xem</span>
                    </button>
                    <button
                      onClick={() => onEdit(item)}
                      className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white py-2 text-xs text-slate-700 active:bg-slate-50"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>Sửa</span>
                    </button>
                    <button
                      onClick={() => onDuplicate(item)}
                      className="flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-100 bg-white py-2 text-xs text-slate-700 active:bg-slate-50"
                    >
                      <Copy className="h-4 w-4" />
                      <span>Chép</span>
                    </button>
                    <button
                      onClick={() => onDelete(item)}
                      className="flex flex-1 items-center justify-center gap-2 rounded-md border border-red-100 bg-red-50 py-2 text-xs text-red-600 active:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Xóa</span>
                    </button>
                  </div>
                  {hasChildren ? (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(item.id)}
                        className="flex items-center gap-2 rounded-md border border-slate-100 p-3 text-sm text-slate-700"
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <span>{isExpanded ? 'Thu gọn danh mục con' : 'Hiển thị danh mục con'}</span>
                      </button>
                      {isExpanded ? <div className="pl-3">{renderMobileChildren(item.id, 1)}</div> : null}
                    </div>
                  ) : null}
                </div>
              </article>
            );
          }}
        />
      </div>

      <div className="hidden rounded-md border border-slate-100 bg-white shadow-sm lg:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-slate-500">
              <th className="p-3 text-xs font-normal">Thông tin danh mục</th>
              <th className="p-3 text-xs font-normal">Phân loại</th>
              <th className="p-3 text-center text-xs font-normal">Số lượng</th>
              <th className="p-3 text-xs font-normal">Trạng thái</th>
              <th className="p-3 font-normal" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <tr key={idx} className="animate-pulse">
                  <td colSpan={5} className="p-3"><div className="h-10 rounded-md bg-slate-100" /></td>
                </tr>
              ))
            ) : pagedParents.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-20 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400">
                      <LayoutGrid className="h-6 w-6" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-slate-900">Không tìm thấy danh mục</p>
                      <p className="text-xs text-slate-500">Thử thay đổi bộ lọc hoặc thêm danh mục mới</p>
                    </div>
                  </div>
                </td>
              </tr>
            ) : pagedParents.map((item) => {
              const hasChildren = (childrenByParent.get(item.id) || []).length > 0;
              const isExpanded = expandedIds.has(item.id);
              return (
                <React.Fragment key={item.id}>
                  <tr className={cn('cursor-pointer hover:bg-slate-50', selectedCategoryId === item.id && 'bg-emerald-50/50')} onClick={() => onSelect(item)}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {hasChildren ? (
                          <button
                            type="button"
                            className="rounded-md border border-slate-100 p-1"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpanded(item.id);
                            }}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        ) : <span className="inline-block h-6 w-6" />}
                        <div className="min-w-0">
                          <p className="truncate text-xs md:text-sm text-slate-900">{item.name}</p>
                          <p className="text-xs text-slate-500">{item.code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={cn(
                        'inline-flex w-20 items-center justify-center rounded-md border py-0.5 text-xs shadow-sm',
                        categoryColorMap[item.type] || categoryColorMap.other
                      )}>
                        {item.typeName}
                      </span>
                    </td>
                    <td className="p-3 text-center text-slate-700">{item.itemCount}</td>
                    <td className="p-3"><StatusBadge status={item.status} /></td>
                    <td className="p-3 text-right" onClick={(event) => event.stopPropagation()}>
                      <ActionMenu
                        items={[
                          { label: 'Xem chi tiết', icon: Eye, onClick: () => onViewDetail(item) },
                          { label: 'Chỉnh sửa', icon: Edit3, onClick: () => onEdit(item) },
                          { label: 'Nhân bản', icon: Copy, onClick: () => onDuplicate(item) },
                          { label: 'Xóa', icon: Trash2, destructive: true, onClick: () => onDelete(item) },
                        ]}
                      />
                    </td>
                  </tr>
                  {hasChildren && isExpanded ? renderDesktopRows(item.id, 1) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-slate-100 bg-white p-3">
        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          className="flex-1"
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={cn('inline-flex rounded-md px-2 py-1 text-xs font-semibold', status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600')}>
      {status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
    </span>
  );
}
